import { useState, useEffect, useCallback, useRef } from 'react';
import {
  Bell, CheckCircle, XCircle, RefreshCw, MessageSquare,
  Copy, Filter, TrendingUp, Clock, AlertTriangle, Wifi,
} from 'lucide-react';
import apiService from '../services/apiService';
import useNotificacoes from '../hooks/useNotificacoes';

// ─── Helpers ──────────────────────────────────────────────────────────────────

function fmt(val) {
  return (val || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

function fmtDataHora(d) {
  if (!d) return '—';
  return new Date(d).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' });
}

const TIER_CORES = {
  A: 'bg-yellow-50 border-yellow-400 text-yellow-700',
  B: 'bg-slate-50  border-slate-400  text-slate-700',
  C: 'bg-orange-50 border-orange-400 text-orange-700',
  D: 'bg-red-50    border-red-400    text-red-700',
  E: 'bg-gray-100  border-gray-400   text-gray-600',
};

const TIER_LABELS = { A: 'Ouro', B: 'Prata', C: 'Bronze', D: 'Risco', E: 'Bloqueado' };

function TierBadge({ tier }) {
  const cls = TIER_CORES[tier] || TIER_CORES.C;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold border ${cls}`}>
      {TIER_LABELS[tier] || tier}
    </span>
  );
}

function StatusBadge({ status }) {
  if (status === 'enviado') {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-green-50 border border-green-300 text-green-700">
        <CheckCircle size={10} /> Enviado
      </span>
    );
  }
  if (status === 'pendente') {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-blue-50 border border-blue-300 text-blue-700">
        <Clock size={10} /> Pendente
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-red-50 border border-red-300 text-red-700">
      <XCircle size={10} /> Erro
    </span>
  );
}

// ─── Card de alerta ───────────────────────────────────────────────────────────

function AlertaCard({ alerta, onReenviar, enviandoId, destaque }) {
  const [copiado, setCopiado] = useState(false);

  function copiar() {
    if (!alerta.mensagem_enviada) return;
    navigator.clipboard.writeText(alerta.mensagem_enviada);
    setCopiado(true);
    setTimeout(() => setCopiado(false), 2000);
  }

  const enviando = enviandoId === alerta.id;

  return (
    <div className={`bg-white rounded-xl border-l-4 shadow-sm transition-all duration-300
      ${destaque ? 'border-green-500 ring-2 ring-green-200 animate-pulse-once' : 'border-blue-400'}`}>

      <div className="p-4">
        {/* Cabeçalho */}
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="font-semibold text-gray-900 truncate">{alerta.nome}</p>
            <p className="text-xs text-gray-500 mt-0.5">{alerta.fone || 'Sem telefone'}</p>
          </div>
          <div className="flex flex-col items-end gap-1 flex-shrink-0">
            <TierBadge tier={alerta.tier_no_envio} />
            <StatusBadge status={alerta.status_envio} />
          </div>
        </div>

        {/* Dados do alerta */}
        <div className="mt-3 grid grid-cols-3 gap-2 text-center">
          <div className="bg-gray-50 rounded-lg py-2 px-1">
            <p className="text-xs text-gray-500">Score</p>
            <p className={`font-bold text-sm ${alerta.score_no_envio >= 700 ? 'text-green-700' : alerta.score_no_envio >= 500 ? 'text-yellow-600' : 'text-orange-600'}`}>
              {alerta.score_no_envio}
            </p>
          </div>
          <div className="bg-green-50 rounded-lg py-2 px-1">
            <p className="text-xs text-gray-500">Limite liberado</p>
            <p className="font-bold text-sm text-green-700">{fmt(alerta.limite_comunicado)}</p>
          </div>
          <div className="bg-gray-50 rounded-lg py-2 px-1">
            <p className="text-xs text-gray-500">Data</p>
            <p className="font-bold text-xs text-gray-700">{fmtDataHora(alerta.enviado_em)}</p>
          </div>
        </div>

        {/* Origem */}
        <p className="mt-2 text-xs text-gray-400">
          {alerta.enviado_por === 'sistema_automatico' ? '🤖 Detectado automaticamente' : '👤 Enviado pelo operador'}
        </p>

        {/* Erro */}
        {alerta.erro_detalhe && (
          <div className="mt-2 flex items-start gap-2 p-2 bg-red-50 border border-red-200 rounded-lg">
            <AlertTriangle size={12} className="text-red-500 mt-0.5 flex-shrink-0" />
            <p className="text-xs text-red-600">{alerta.erro_detalhe}</p>
          </div>
        )}
      </div>

      {/* Ações */}
      {alerta.mensagem_enviada && alerta.fone && (
        <div className="px-4 pb-3 flex items-center gap-2">
          <button
            onClick={() => onReenviar(alerta)}
            disabled={enviando}
            className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-green-600 hover:bg-green-700 text-white text-xs font-medium rounded-lg transition-colors disabled:opacity-50"
          >
            {enviando
              ? <><RefreshCw size={12} className="animate-spin" /> Enviando…</>
              : <><MessageSquare size={12} /> Reenviar ao cliente</>
            }
          </button>
          <button
            onClick={copiar}
            className="p-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            title="Copiar mensagem"
          >
            {copiado
              ? <CheckCircle size={14} className="text-green-500" />
              : <Copy size={14} className="text-gray-500" />
            }
          </button>
        </div>
      )}
    </div>
  );
}

// ─── Resumo ───────────────────────────────────────────────────────────────────

function ResumoCards({ alertas }) {
  const total    = alertas.length;
  const enviados = alertas.filter(a => a.status_envio === 'enviado').length;
  const erros    = alertas.filter(a => a.status_envio === 'erro').length;
  const hoje     = alertas.filter(a => {
    if (!a.enviado_em) return false;
    const d = new Date(a.enviado_em);
    const now = new Date();
    return d.toDateString() === now.toDateString();
  }).length;

  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
      {[
        { label: 'Total de alertas',   value: total,    color: 'blue',   icon: Bell        },
        { label: 'Enviados',           value: enviados, color: 'green',  icon: CheckCircle },
        { label: 'Alertas hoje',       value: hoje,     color: 'purple', icon: TrendingUp  },
        { label: 'Com erro',           value: erros,    color: 'red',    icon: XCircle     },
      ].map(({ label, value, color, icon: Icon }) => (
        <div key={label} className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm text-gray-500">{label}</p>
            <div className={`w-8 h-8 rounded-lg flex items-center justify-center bg-${color}-50`}>
              <Icon className={`w-4 h-4 text-${color}-600`} />
            </div>
          </div>
          <p className="text-2xl font-bold text-gray-900">{value}</p>
        </div>
      ))}
    </div>
  );
}

// ─── Página principal ─────────────────────────────────────────────────────────

export default function AlertasCrediario() {
  const [alertas, setAlertas]       = useState([]);
  const [loading, setLoading]       = useState(false);
  const [erro, setErro]             = useState('');
  const [filtroStatus, setFiltroStatus] = useState('todos');
  const [enviandoId, setEnviandoId] = useState(null);
  const [toast, setToast]           = useState(null);
  const [novosIds, setNovosIds]     = useState(new Set());

  // Hook de notificações SSE existente
  const { notificacoes } = useNotificacoes();
  const ultimoNotifId = useRef(null);

  function showToast(msg, tipo = 'success') {
    setToast({ msg, tipo });
    setTimeout(() => setToast(null), 4000);
  }

  const carregar = useCallback(async () => {
    setLoading(true);
    setErro('');
    try {
      const resp = await apiService.get('/api/credito-rotativo/alertas?porPagina=100');
      setAlertas(resp.data.alertas || []);
    } catch (e) {
      setErro('Erro ao carregar alertas. Verifique a conexão com o backend.');
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { carregar(); }, [carregar]);

  // Recarrega lista quando chega novo SSE de upgrade_credito
  useEffect(() => {
    const novo = notificacoes.find(
      n => n.tipo === 'upgrade_credito' && n.id !== ultimoNotifId.current,
    );
    if (!novo) return;
    ultimoNotifId.current = novo.id;

    // Recarrega e marca o registro mais recente como destaque
    carregar().then(() => {
      // Após reload, marca os IDs mais novos por 5s
      setNovosIds(prev => {
        const next = new Set(prev);
        // O alerta mais novo será o primeiro após reload; usamos timestamp como proxy
        next.add(`novo_${Date.now()}`);
        return next;
      });
      setTimeout(() => setNovosIds(new Set()), 5000);
    });
  }, [notificacoes, carregar]);

  async function reenviarParaCliente(alerta) {
    if (!alerta.fone || !alerta.mensagem_enviada) {
      showToast('Alerta sem telefone ou mensagem.', 'error');
      return;
    }
    setEnviandoId(alerta.id);
    try {
      const resp = await apiService.post(`/api/credito-rotativo/alertas/${alerta.id}/reenviar`, {
        ordem_cliente: alerta.ordem_cliente,
        nome:          alerta.nome,
        fone:          alerta.fone,
        mensagem:      alerta.mensagem_enviada,
        score:         alerta.score_no_envio,
        tier:          alerta.tier_no_envio,
        novo_limite:   alerta.limite_comunicado,
      });
      if (resp.data.success) {
        showToast(`Mensagem reenviada para ${(alerta.nome || '').split(' ')[0]}!`);
        carregar();
      } else {
        showToast(`Falha: ${resp.data.error || 'Erro desconhecido'}`, 'error');
      }
    } catch (e) {
      showToast('Erro ao reenviar mensagem.', 'error');
    } finally {
      setEnviandoId(null);
    }
  }

  const alertasFiltrados = alertas.filter(a => {
    if (filtroStatus === 'todos') return true;
    return a.status_envio === filtroStatus;
  });

  // Considera os primeiros itens como "novos" quando há destaque ativo
  const isNovo = (alerta, idx) => novosIds.size > 0 && idx === 0;

  return (
    <div className="max-w-7xl mx-auto">

      {/* Toast */}
      {toast && (
        <div className={`fixed top-4 right-4 z-50 flex items-center gap-3 px-4 py-3 rounded-xl shadow-lg border text-sm font-medium
          ${toast.tipo === 'success' ? 'bg-green-50 border-green-300 text-green-800' : 'bg-red-50 border-red-300 text-red-800'}`}>
          {toast.tipo === 'success'
            ? <CheckCircle size={16} className="text-green-600" />
            : <XCircle    size={16} className="text-red-600" />}
          {toast.msg}
        </div>
      )}

      {/* Cabeçalho */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Alertas de Crédito</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            Clientes que se tornaram elegíveis para aumento de limite — notificados automaticamente
          </p>
        </div>
        <div className="flex items-center gap-3">
          {/* Indicador SSE */}
          <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-green-50 border border-green-200">
            <Wifi size={13} className="text-green-600" />
            <span className="text-xs font-medium text-green-700">Tempo real</span>
          </div>
          <button
            onClick={carregar}
            disabled={loading}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors disabled:opacity-50"
          >
            <RefreshCw size={15} className={loading ? 'animate-spin' : ''} />
            {loading ? 'Carregando…' : 'Atualizar'}
          </button>
        </div>
      </div>

      {/* Erro */}
      {erro && (
        <div className="mb-4 flex items-center gap-3 p-4 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700">
          <XCircle size={16} className="flex-shrink-0" />
          {erro}
        </div>
      )}

      {/* Cards de resumo */}
      {alertas.length > 0 && <ResumoCards alertas={alertas} />}

      {/* Explicação do fluxo */}
      <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-xl text-sm text-blue-800">
        <p className="font-semibold mb-1">Como funciona</p>
        <p>
          Quando o sistema detecta o pagamento de uma parcela e o cliente atinge os critérios de elegibilidade,
          ele é listado aqui <strong>automaticamente</strong> e uma notificação push é enviada ao sistema
          juntamente com uma mensagem WhatsApp para os responsáveis configurados.
          Você pode reenviar a mensagem diretamente ao cliente usando o botão abaixo de cada card.
        </p>
      </div>

      {/* Filtro */}
      <div className="bg-white rounded-xl border border-gray-200 p-4 mb-4 flex items-center gap-3">
        <Filter size={15} className="text-gray-400 flex-shrink-0" />
        <select
          value={filtroStatus}
          onChange={e => setFiltroStatus(e.target.value)}
          className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
        >
          <option value="todos">Todos os alertas</option>
          <option value="enviado">Enviados</option>
          <option value="pendente">Pendentes</option>
          <option value="erro">Com erro</option>
        </select>
        {!loading && (
          <p className="text-sm text-gray-500 ml-auto">
            <strong>{alertasFiltrados.length}</strong> alerta{alertasFiltrados.length !== 1 ? 's' : ''}
          </p>
        )}
      </div>

      {/* Estado de loading */}
      {loading && (
        <div className="flex flex-col items-center justify-center py-20 text-gray-400">
          <RefreshCw size={32} className="animate-spin mb-4 text-blue-500" />
          <p className="font-medium text-gray-600">Carregando alertas…</p>
        </div>
      )}

      {/* Estado vazio */}
      {!loading && alertas.length === 0 && !erro && (
        <div className="flex flex-col items-center justify-center py-20 text-gray-400">
          <Bell size={48} className="mb-4 text-gray-300" />
          <p className="font-medium text-gray-600">Nenhum alerta registrado ainda</p>
          <p className="text-sm mt-1">Os alertas aparecem automaticamente quando um cliente paga parcelas e se torna elegível</p>
        </div>
      )}

      {/* Grid de cards */}
      {!loading && alertasFiltrados.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {alertasFiltrados.map((alerta, idx) => (
            <AlertaCard
              key={alerta.id}
              alerta={alerta}
              onReenviar={reenviarParaCliente}
              enviandoId={enviandoId}
              destaque={isNovo(alerta, idx)}
            />
          ))}
        </div>
      )}

      {/* Sem resultados após filtro */}
      {!loading && alertas.length > 0 && alertasFiltrados.length === 0 && (
        <div className="flex flex-col items-center justify-center py-12 text-gray-400">
          <Bell size={32} className="mb-3 text-gray-300" />
          <p className="font-medium text-gray-600">Nenhum alerta para este filtro</p>
          <button onClick={() => setFiltroStatus('todos')} className="mt-3 text-sm text-blue-600 hover:underline">
            Limpar filtro
          </button>
        </div>
      )}
    </div>
  );
}
